import { lazy, Suspense } from "react"
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { Toaster } from "sonner"
import { ProtectedRoute } from "@/components/ProtectedRoute"
import { ProRoute } from "@/components/ProRoute"
import { AppLayout } from "@/components/layout/AppLayout"
import { PwaBanner } from "@/components/PwaBanner"
import { ErrorBoundary } from "@/components/ErrorBoundary"
import { useAuthStore } from "@/store/authStore"
import { LoginPage } from "@/pages/LoginPage"
import { RegisterPage } from "@/pages/RegisterPage"
import { ForgotPasswordPage } from "@/pages/ForgotPasswordPage"
import { ResetPasswordPage } from "@/pages/ResetPasswordPage"
import { VerifyEmailPage } from "@/pages/VerifyEmailPage"
import { BillingSuccessPage } from "@/pages/BillingSuccessPage"

// Lazy-loaded page chunks — each becomes its own JS chunk
const DashboardPage             = lazy(() => import("@/pages/DashboardPage").then(m => ({ default: m.DashboardPage })))
const TransactionsPage          = lazy(() => import("@/pages/TransactionsPage").then(m => ({ default: m.TransactionsPage })))
const BankAccountsPage          = lazy(() => import("@/pages/BankAccountsPage").then(m => ({ default: m.BankAccountsPage })))
const CreditCardsPage           = lazy(() => import("@/pages/CreditCardsPage").then(m => ({ default: m.CreditCardsPage })))
const CategoriesPage            = lazy(() => import("@/pages/CategoriesPage").then(m => ({ default: m.CategoriesPage })))
const BudgetsPage               = lazy(() => import("@/pages/BudgetsPage").then(m => ({ default: m.BudgetsPage })))
const GoalsPage                 = lazy(() => import("@/pages/GoalsPage").then(m => ({ default: m.GoalsPage })))
const InvestmentsPage           = lazy(() => import("@/pages/InvestmentsPage").then(m => ({ default: m.InvestmentsPage })))
const AiAnalysisPage            = lazy(() => import("@/pages/AiAnalysisPage").then(m => ({ default: m.AiAnalysisPage })))
const ReportsPage               = lazy(() => import("@/pages/ReportsPage").then(m => ({ default: m.ReportsPage })))
const SettingsPage              = lazy(() => import("@/pages/SettingsPage").then(m => ({ default: m.SettingsPage })))
const ImportsPage               = lazy(() => import("@/pages/ImportsPage").then(m => ({ default: m.ImportsPage })))
const RecurringTransactionsPage = lazy(() => import("@/pages/RecurringTransactionsPage").then(m => ({ default: m.RecurringTransactionsPage })))
const ChatPage                  = lazy(() => import("@/pages/ChatPage").then(m => ({ default: m.ChatPage })))
const AutoRulesPage             = lazy(() => import("@/pages/AutoRulesPage").then(m => ({ default: m.AutoRulesPage })))
const CalculatorsPage           = lazy(() => import("@/pages/CalculatorsPage").then(m => ({ default: m.CalculatorsPage })))
const DebtsPage                 = lazy(() => import("@/pages/DebtsPage").then(m => ({ default: m.DebtsPage })))
const PricingPage               = lazy(() => import("@/pages/PricingPage").then(m => ({ default: m.PricingPage })))
const NotificationsPage         = lazy(() => import("@/pages/NotificationsPage").then(m => ({ default: m.NotificationsPage })))
const HomePage                  = lazy(() => import("@/pages/HomePage").then(m => ({ default: m.HomePage })))
const HelpPage                  = lazy(() => import("@/pages/HelpPage").then(m => ({ default: m.HelpPage })))
const PrivacyPage               = lazy(() => import("@/pages/legal/PrivacyPage").then(m => ({ default: m.PrivacyPage })))
const TermsPage                 = lazy(() => import("@/pages/legal/TermsPage").then(m => ({ default: m.TermsPage })))
const GroupPage                 = lazy(() => import("@/pages/GroupPage").then(m => ({ default: m.GroupPage })))
const InvitePage                = lazy(() => import("@/pages/InvitePage").then(m => ({ default: m.InvitePage })))
const NotFoundPage              = lazy(() => import("@/pages/NotFoundPage").then(m => ({ default: m.NotFoundPage })))

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, staleTime: 1000 * 60 },
  },
})

function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="flex flex-col items-center gap-3 text-muted-foreground">
        <div className="h-6 w-6 rounded-full border-2 border-primary border-t-transparent animate-spin" />
        <span className="text-[13px]">Carregando...</span>
      </div>
    </div>
  )
}

function HomeRoute() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  return isAuthenticated ? <Navigate to="/dashboard" replace /> : <HomePage />
}

function AppShell() {
  return (
    <AppLayout>
      <Outlet />
    </AppLayout>
  )
}

export default function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <Toaster richColors position="top-right" />
        <PwaBanner />
        <BrowserRouter>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Visitante vê a homepage; quem já entrou vai direto ao painel */}
              <Route path="/" element={<HomeRoute />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/verify-email" element={<VerifyEmailPage />} />
              <Route path="/billing/success" element={<BillingSuccessPage />} />
              <Route path="/privacidade" element={<PrivacyPage />} />
              <Route path="/termos" element={<TermsPage />} />
              {/* Convite da carteira conjunta: pública para guardar o token antes do login (ADR-021) */}
              <Route path="/invite" element={<InvitePage />} />

              <Route element={<ProtectedRoute />}>
                <Route element={<AppShell />}>
                  {/* Free + Pro */}
                  <Route path="dashboard" element={<ErrorBoundary><DashboardPage /></ErrorBoundary>} />
                  <Route path="transactions" element={<ErrorBoundary><TransactionsPage /></ErrorBoundary>} />
                  <Route path="bank-accounts" element={<ErrorBoundary><BankAccountsPage /></ErrorBoundary>} />
                  <Route path="categories" element={<ErrorBoundary><CategoriesPage /></ErrorBoundary>} />
                  <Route path="reports" element={<ErrorBoundary><ReportsPage /></ErrorBoundary>} />
                  <Route path="settings" element={<ErrorBoundary><SettingsPage /></ErrorBoundary>} />
                  <Route path="pricing" element={<ErrorBoundary><PricingPage /></ErrorBoundary>} />
                  <Route path="notifications" element={<ErrorBoundary><NotificationsPage /></ErrorBoundary>} />
                  <Route path="help" element={<ErrorBoundary><HelpPage /></ErrorBoundary>} />
                  <Route path="group" element={<ErrorBoundary><GroupPage /></ErrorBoundary>} />
                  {/* Free com limite de quantidade (ADR-009) */}
                  <Route path="credit-cards" element={<ErrorBoundary><CreditCardsPage /></ErrorBoundary>} />
                  <Route path="chat" element={<ErrorBoundary><ChatPage /></ErrorBoundary>} />

                  {/* Pro only */}
                  <Route element={<ProRoute />}>
                    <Route path="investments" element={<ErrorBoundary><InvestmentsPage /></ErrorBoundary>} />
                    <Route path="debts" element={<ErrorBoundary><DebtsPage /></ErrorBoundary>} />
                    <Route path="budgets" element={<ErrorBoundary><BudgetsPage /></ErrorBoundary>} />
                    <Route path="goals" element={<ErrorBoundary><GoalsPage /></ErrorBoundary>} />
                    <Route path="recurring" element={<ErrorBoundary><RecurringTransactionsPage /></ErrorBoundary>} />
                    <Route path="auto-rules" element={<ErrorBoundary><AutoRulesPage /></ErrorBoundary>} />
                    <Route path="ai-analysis" element={<ErrorBoundary><AiAnalysisPage /></ErrorBoundary>} />
                    <Route path="imports" element={<ErrorBoundary><ImportsPage /></ErrorBoundary>} />
                    <Route path="calculators" element={<ErrorBoundary><CalculatorsPage /></ErrorBoundary>} />
                  </Route>
                </Route>
              </Route>

              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
  )
}
